import { useEffect, useRef, useState } from 'react';
import { Bell, CheckCheck, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 20_000;

/**
 * Fixed-position notification bell for logged-in portals. Shows the current
 * unread count, and on click opens a dropdown of recent history (last 50).
 * Clicking a row marks it read; "Mark all read" clears the badge.
 */
export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [authed, setAuthed] = useState(false);
  const panelRef = useRef(null);

  const load = async (opts = { history: false }) => {
    const url = opts.history
      ? `${API}/api/notifications?include_read=true&limit=50`
      : `${API}/api/notifications?limit=50`;
    const res = await fetch(url, { credentials: 'include' });
    if (!res.ok) { if (res.status === 401) setAuthed(false); return null; }
    setAuthed(true);
    return res.json();
  };

  // Poll unread count while mounted.
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      const data = await load({ history: false });
      if (cancelled || !Array.isArray(data)) return;
      setUnread(data.length);
    };
    tick();
    const id = setInterval(tick, POLL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // Close on outside click / escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const openPanel = async () => {
    setOpen(true);
    const data = await load({ history: true });
    if (Array.isArray(data)) setItems(data);
  };

  const dismiss = async (id) => {
    await fetch(`${API}/api/notifications/${id}/dismiss`, { method: 'POST', credentials: 'include' });
    setItems((list) => list.map((n) => (n.id === id ? { ...n, read_at: new Date().toISOString() } : n)));
    setUnread((c) => Math.max(0, c - 1));
  };

  const markAllRead = async () => {
    await fetch(`${API}/api/notifications/read-all`, { method: 'POST', credentials: 'include' });
    setItems((list) => list.map((n) => (n.read_at ? n : { ...n, read_at: new Date().toISOString() })));
    setUnread(0);
  };

  if (!authed) return null;

  return (
    <div className="fixed top-4 right-4 z-[60]" ref={panelRef} data-testid="notification-bell-root">
      <button
        type="button"
        onClick={() => (open ? setOpen(false) : openPanel())}
        aria-label="Notifications"
        data-testid="notification-bell-btn"
        className="relative w-11 h-11 rounded-full bg-white border border-tge-navy/10 shadow-sm hover:shadow-md hover:border-tge-navy/25 transition flex items-center justify-center text-tge-navy"
      >
        <Bell size={18} />
        {unread > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-600 text-white text-[10px] font-bold flex items-center justify-center"
            data-testid="notification-unread-count"
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-[380px] max-h-[70vh] overflow-hidden bg-white rounded-2xl shadow-2xl border border-tge-navy/10 flex flex-col"
          data-testid="notification-panel"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-tge-navy/5">
            <div className="text-sm font-semibold text-tge-navy">Notifications</div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={markAllRead}
                disabled={unread === 0}
                className="text-[11px] text-tge-muted hover:text-tge-navy inline-flex items-center gap-1 disabled:opacity-40"
                data-testid="notification-mark-all-read"
              >
                <CheckCheck size={12} /> Mark all read
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close"
                className="text-tge-muted hover:text-tge-navy"
              >
                <X size={14} />
              </button>
            </div>
          </div>

          <div className="overflow-y-auto flex-1 divide-y divide-tge-cream" data-testid="notification-list">
            {items.length === 0 ? (
              <div className="px-4 py-10 text-center text-xs text-tge-muted">
                You are all caught up.
              </div>
            ) : (
              items.map((n) => {
                const isUnread = !n.read_at;
                return (
                  <button
                    key={n.id}
                    type="button"
                    onClick={() => isUnread && dismiss(n.id)}
                    className={`w-full text-left px-4 py-3 hover:bg-tge-cream-soft/50 transition ${isUnread ? 'bg-amber-50/40' : ''}`}
                    data-testid={`notification-item-${n.id}`}
                  >
                    <div className="flex items-start gap-2">
                      {isUnread && <span className="mt-1.5 w-2 h-2 rounded-full bg-tge-gold shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <div className={`text-sm text-tge-navy ${isUnread ? 'font-semibold' : ''}`}>
                          {n.title || 'Notification'}
                        </div>
                        {n.body && (
                          <div className="text-xs text-tge-muted mt-0.5 line-clamp-3">{n.body}</div>
                        )}
                        <div className="text-[10px] text-tge-navy/40 mt-1">
                          {n.created_at ? new Date(n.created_at).toLocaleString() : ''}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
